'use client'

import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { BodyText, LabelText } from '@/components/ui/typography'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { Reveal } from '@/components/ui/reveal'
import { IconArrowRight, IconCalendar } from '@tabler/icons-react'
import { useTagTranslations } from '@/hooks/use-tag-translations'
import type { BlogMetadata, BlogTag } from '@/content/blogs'

// Component to handle image loading with fallback gradient
function BlogCardImage({
  src,
  alt,
  eager = false
}: {
  src?: string
  alt: string
  eager?: boolean
}) {
  const [hasError, setHasError] = useState(false)

  if (!src || hasError) {
    return (
      <div className="from-primary/10 via-background to-primary/5 flex h-full w-full items-center justify-center bg-linear-to-br" />
    )
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill
      className="object-cover"
      sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
      loading={eager ? 'eager' : 'lazy'}
      onError={() => setHasError(true)}
    />
  )
}

interface BlogListClientProps {
  blogs: BlogMetadata[]
  blogTags: BlogTag[]
  locale: string
}

export function BlogListClient({
  blogs: initialBlogs,
  blogTags,
  locale
}: BlogListClientProps) {
  const t = useTranslations('blog')
  const { translateBlogTag } = useTagTranslations()
  const [selectedTag, setSelectedTag] = useState<BlogTag | undefined>()

  const filteredBlogs = useMemo(() => {
    if (!selectedTag) return initialBlogs
    return initialBlogs.filter((blog) => blog.tags.includes(selectedTag))
  }, [initialBlogs, selectedTag])

  return (
    <>
      {blogTags.length > 0 && (
        <Reveal
          className="bg-card/60 rounded-3xl border p-5 shadow-sm"
          variant="fadeUpSoft"
          viewportAmount={0.1}
          viewportMargin="0px 0px 12% 0px"
          transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="-mx-2 overflow-x-auto pb-1">
            <div className="flex min-w-max items-center gap-2 px-2">
              <LabelText className="text-muted-foreground shrink-0 text-xs tracking-widest uppercase">
                {t('filterByTag')}
              </LabelText>
              <FilterGroup
                options={blogTags}
                value={selectedTag}
                onChange={setSelectedTag}
              />
            </div>
          </div>
        </Reveal>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredBlogs.map((blog, index) => (
          <Reveal
            key={blog.slug}
            as="article"
            variant="fadeUpSoft"
            className="border-border/70 bg-card rounded-2xl border p-5 shadow-sm transition-shadow duration-200 hover:shadow-lg"
            viewportAmount={0.08}
            viewportMargin="0px 0px 16% 0px"
            delay={(index % 3) * 0.035}
            transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative -mx-5 -mt-5 mb-4 aspect-video overflow-hidden rounded-t-2xl">
              <BlogCardImage
                src={blog.heroImage}
                alt={blog.title}
                eager={index < 6}
              />
            </div>
            <div className="mb-3 flex flex-wrap gap-2">
              {blog.tags.slice(0, 2).map((tag) => (
                <Badge key={tag} variant="outline" size="sm">
                  {translateBlogTag(tag)}
                </Badge>
              ))}
            </div>
            <h3 className="mb-2 text-xl font-semibold">{blog.title}</h3>
            <BodyText className="text-muted-foreground mb-4 line-clamp-2 text-sm">
              {blog.description}
            </BodyText>
            <div className="text-muted-foreground mb-4 flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <IconCalendar className="h-3 w-3" />
                {new Date(
                  blog.updatedAt || blog.publishedAt
                ).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            <Button
              asChild
              variant="ghost"
              className="w-full justify-start px-0"
            >
              <Link href={`/blog/${blog.slug}`}>
                {t('readArticle')}
                <IconArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </Reveal>
        ))}

        {filteredBlogs.length === 0 && (
          <Reveal
            className="border-border/70 bg-card/70 col-span-full rounded-2xl border border-dashed p-6 text-center"
            variant="fadeUpSoft"
            viewportAmount={0.1}
            viewportMargin="0px 0px 12% 0px"
          >
            <BodyText className="text-muted-foreground">
              {t('noPostsFound')}
            </BodyText>
          </Reveal>
        )}
      </div>
    </>
  )
}

function FilterGroup({
  options,
  value,
  onChange
}: {
  options: BlogTag[]
  value?: BlogTag
  onChange: (next?: BlogTag) => void
}) {
  const t = useTranslations('common')
  const { translateBlogTag } = useTagTranslations()

  return (
    <>
      <Button
        variant={!value ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange(undefined)}
      >
        {t('all')}
      </Button>
      {options.map((option) => (
        <Button
          key={option}
          variant={value === option ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(option)}
        >
          {translateBlogTag(option)}
        </Button>
      ))}
    </>
  )
}
